import { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  RotateCcw,
  AlertTriangle,
  Calendar,
  User,
  FileText,
  Loader2,
  Info,
} from 'lucide-react'
import type { TemplateVersion } from '@/lib/api/types'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'

interface RestoreVersionDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  version: TemplateVersion | null
  onConfirm: () => Promise<void>
}

export function RestoreVersionDialog({
  open,
  onOpenChange,
  version,
  onConfirm,
}: RestoreVersionDialogProps) {
  const [loading, setLoading] = useState(false)

  const handleConfirm = async () => {
    setLoading(true)
    try {
      await onConfirm()
    } finally {
      setLoading(false)
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  if (!version) return null

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-gradient-to-br from-amber-400 to-orange-600">
              <RotateCcw className="h-5 w-5 text-white" />
            </div>
            <div>
              <DialogTitle>Restore Version {version.versionNumber}</DialogTitle>
              <DialogDescription>
                Are you sure you want to restore this version?
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Warning Banner */}
          <div className="flex items-start gap-3 p-4 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
            <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
            <div className="space-y-1">
              <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
                This action will create a new version
              </p>
              <p className="text-sm text-amber-700 dark:text-amber-300">
                Restoring this version will create a new version based on the selected version's content.
                The current version will be preserved in the version history.
              </p>
            </div>
          </div>

          {/* Version Details Card */}
          <div className="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="p-4 bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <span className="text-lg font-semibold text-gray-900 dark:text-white">
                  Version {version.versionNumber}
                </span>
                <div className="flex items-center gap-2">
                  {version.isPublished && (
                    <Badge variant="default" className="text-xs">
                      Published
                    </Badge>
                  )}
                </div>
              </div>
            </div>

            <div className="p-4 space-y-3">
              {/* Created By */}
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-gray-100 dark:bg-gray-800">
                  <User className="h-4 w-4 text-gray-500" />
                </div>
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Created by</p>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    {version.createdByName || version.createdBy}
                  </p>
                </div>
              </div>

              {/* Created At */}
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-gray-100 dark:bg-gray-800">
                  <Calendar className="h-4 w-4 text-gray-500" />
                </div>
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Created</p>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    {formatDate(version.createdAt)}
                  </p>
                </div>
              </div>

              {/* Variables Count */}
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-gray-100 dark:bg-gray-800">
                  <FileText className="h-4 w-4 text-gray-500" />
                </div>
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Variables</p>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    {version.variables?.length || 0} variable{(version.variables?.length || 0) !== 1 ? 's' : ''}
                  </p>
                </div>
              </div>

              {/* Comment */}
              {version.changeDescription && (
                <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Version Notes</p>
                  <p className="text-sm text-gray-700 dark:text-gray-300">
                    {version.changeDescription}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* What Will Happen */}
          <div className="rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 p-4">
            <div className="flex items-start gap-3">
              <Info className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
              <div className="space-y-2">
                <p className="text-sm font-medium text-blue-800 dark:text-blue-200">
                  What will happen:
                </p>
                <ul className="text-sm text-blue-700 dark:text-blue-300 space-y-1 list-disc list-inside">
                  <li>A new version will be created with the content from version {version.versionNumber}</li>
                  <li>All variables will be restored to their state in version {version.versionNumber}</li>
                  <li>The current version will remain in version history</li>
                  <li>Any unsaved changes to the current template will be overwritten</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            variant="default"
            onClick={handleConfirm}
            disabled={loading}
            className="gap-2"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Restoring...
              </>
            ) : (
              <>
                <RotateCcw className="h-4 w-4" />
                Restore Version {version.versionNumber}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
